import ApplicationModel, { attr } from '../ApplicationModel';
import InitiativeFilter from '../filters/InitiativeFilter';
import IterationFilter from '../filters/IterationFilter';
import ProjectFilter from '../filters/ProjectFilter';
import ReleaseFilter from '../filters/ReleaseFilter';
import TeamFilter from '../filters/TeamFilter';
import UserFilter from '../filters/UserFilter';
import Fragment from '../Fragment';
import { model } from '../modelBuilder';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksDeliveryRisksChartAttributes } from './generated/BookmarksDeliveryRisksChartAttributes';
import { type RecordUnion } from './generated/types';
import Initiative from './Initiative';
import Iteration from './Iteration';
import Project from './Project';
import Release from './Release';
import User from './User';

interface BookmarksDeliveryRisksChart
  extends BookmarksDeliveryRisksChartAttributes,
    HasBookmarkFilters<{
      releases: ReleaseFilter;
      projects: ProjectFilter;
      teams: TeamFilter;
      initiatives: InitiativeFilter;
      teamMembers: UserFilter;
      iterations: IterationFilter;
    }> {}

@model
@HasBookmarkFilters
class BookmarksDeliveryRisksChart extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksDeliveryRisksChart' =
    'BookmarksDeliveryRisksChart';
  get typename(): 'BookmarksDeliveryRisksChart' {
    return 'BookmarksDeliveryRisksChart';
  }

  static internalTypename: 'Bookmarks::DeliveryRisksChart' =
    'Bookmarks::DeliveryRisksChart';
  get internalTypename(): 'Bookmarks::DeliveryRisksChart' {
    return 'Bookmarks::DeliveryRisksChart';
  }

  static bookmarkName = 'Bookmarks::DeliveryRisksChart';

  static inputType = 'DeliveryRisksChartAttributes!';

  static fields = {
    dataGroup: attr({ writable: true, readable: true }),
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  /**
   * Initialize the filters
   */
  async createFilters() {
    const [releases, projects, teams, initiatives, teamMembers, iterations] =
      await Promise.all([
        this.selectedReleases(),
        this.selectedProjects(),
        this.selectedTeams(),
        this.selectedInitiatives(),
        this.selectedUsers(),
        this.selectedIterations(),
      ]);

    this.local.featureTypes = this.filters.featureTypes || [];
    this.local.featureTypeIds = {};

    return {
      releases: new ReleaseFilter(this.filters.releases, releases),
      projects: new ProjectFilter(this.filters.projects, projects),
      teams: new TeamFilter(
        teams.map(([, id]) => id),
        teams
      ),
      initiatives: new InitiativeFilter(this.filters.initiatives, initiatives),
      teamMembers: new UserFilter(this.filters.teamMembers, teamMembers),
      iterations: new IterationFilter(this.filters.iterations, iterations),
    };
  }

  canFilter(_scope) {
    return true;
  }

  async selectedReleases(): Promise<[string, any][]> {
    const releaseIds = this.filters.releases || [];

    if (releaseIds.length > 0) {
      const releases = await Release.select(['id', 'name'])
        .merge({ project: ['referencePrefix'] })
        .where({ id: releaseIds })
        .all();

      return releases.map(r => [
        `${r.project.referencePrefix} - ${r.name}`,
        r.id || '',
      ]);
    }

    return [];
  }

  async selectedProjects(): Promise<[string, any][]> {
    const projectIds = this.filters.projects || [];

    if (projectIds.length > 0) {
      const projects = await Project.select(['id', 'name', 'referencePrefix'])
        .where({ id: projectIds })
        .all();

      return projects.map(p => [p.name, p.id || '']);
    }

    return [];
  }

  async selectedTeams(): Promise<[string, RecordId][]> {
    if (!this.project?.isProductLine) {
      return [];
    }
    const childIds = this.project.children.map(c => c.id);
    const teamIds = this.filters.teams?.length ? this.filters.teams : childIds;

    if (teamIds.length > 0) {
      const teams = await Project.select(['id', 'name'])
        .where({ id: teamIds })
        .all();

      return teams.map(t => [t.name, t.id]);
    }
    return [];
  }

  async selectedInitiatives(): Promise<[string, any][]> {
    const initiativeIds = this.filters.initiatives || [];

    if (initiativeIds.length > 0) {
      const initiatives = await Initiative.select(['id', 'name'])
        .where({ id: initiativeIds })
        .all();

      return initiatives.map(i => [i.name, i.id || '']);
    }

    return [];
  }

  async availableUsers() {
    if (!this.local.users) {
      this.local.users = await User.select('id', 'name', 'initials', 'email')
        .select({ avatarUrl: { size: aha.enums.AvatarSizeEnum.SIZE_40 } })
        .where({ projectId: this.projectId })
        .all();
    }

    return this.local.users;
  }

  async selectedUsers() {
    const userIds = this.filters.teamMembers || [];

    if (userIds.length > 0) {
      const users = await this.availableUsers();
      return users
        .filter(u => userIds.includes(u.id))
        .map(u => [u.name, u.id || '']);
    }
  }

  async availableIterations() {
    if (!this.local.iterations) {
      this.local.iterations = await Iteration.select(['id', 'name'])
        .order({ startDate: 'DESC' })
        .where({ projectId: this.projectId })
        .all();
    }

    return this.local.iterations;
  }

  async selectedIterations() {
    const iterationIds = this.filters.iterations || [];

    if (iterationIds.length > 0) {
      const iterations = await this.availableIterations();

      return iterations
        .filter(i => iterationIds.includes(i.id))
        .map(i => [i.name, i.id || '']);
    }
  }

  async chartData(): Promise<{ workRecords: RecordUnion[] }> {
    const availableTypes = ['Feature', 'Requirement'];
    if (!this.project || this.project.epicsEnabled) {
      availableTypes.unshift('Epic');
    }

    const fragment = new Fragment().fragment(
      availableTypes.reduce(
        (fragment, type) =>
          fragment.fragment(
            new Fragment('', type)
              .union()
              .attr('id', 'referenceNum', 'name', 'riskFactorsCount')
              .fragment(
                new Fragment('release').attr('id', 'name', 'referenceNum')
              )
              .fragment(new Fragment('project').attr('id', 'name'))
              .fragment(new Fragment('team').attr('id'))
              .fragment(
                new Fragment('initiative').attr('id', 'name', 'referenceNum')
              )
              .fragment(new Fragment('assignedToUser').attr('id', 'name'))
              .fragment(new Fragment('iteration').attr('id', 'name'))
          ),
        new Fragment('workRecords').argument(
          'filters',
          {
            teamId: this.projectId,
            teamWorkflowStatusInternalMeanings: ['NOT_STARTED', 'IN_PROGRESS'],
          },
          'WorkRecordFilters!'
        )
      )
    );

    return ApplicationModel.query(fragment);
  }

  /**
   * Updates the list of all possible filter IDs with `options`. For
   * example, will tell the `UserFilter` about all users once the
   * Selectro is first opened.
   */
  setFilterOptions(scope, options) {
    this.getFilter(scope).setOptions(options);
  }
}

export default BookmarksDeliveryRisksChart;
